/**
 * Receipt numbers look like: R-1781725253898-ogk6t7 (see nextReceiptNum in orders.js).
 * Barcode scanners emit US key codes; with an Arabic keyboard layout those become Arabic letters.
 * Shift+R (scanner uppercase R) maps to ٌ on Arabic layout; unshifted R maps to ق.
 * Preserve hyphens and lowercase suffix; map Arabic layout output back to Latin.
 */
const ARABIC_KEYBOARD_CHAR_TO_LATIN = {
  ش: 'a',
  لا: 'b',
  ؤ: 'c',
  ي: 'd',
  ث: 'e',
  ب: 'f',
  ل: 'g',
  ا: 'h',
  أ: 'h',
  إ: 'h',
  آ: 'h',
  ه: 'i',
  ت: 'j',
  ن: 'k',
  ك: 'l',
  ة: 'm',
  ى: 'n',
  خ: 'o',
  ح: 'p',
  ض: 'q',
  ق: 'r',
  'ٌ': 'r', // Shift+R on Arabic layout (scanner uppercase R)
  س: 's',
  ف: 't',
  ع: 'u',
  ر: 'v',
  ص: 'w',
  ء: 'x',
  غ: 'y',
  ئ: 'z',
  ظ: 'z',
  ز: 'z',
  ذ: '`',
  د: 'd',
  ج: 'j',
  ط: 't',
  و: 'w',
  ـ: '-',
  '_': '-',
};

const ARABIC_INDIC_TO_LATIN = '٠١٢٣٤٥٦٧٨٩';

/** @param {string} raw @param {{ allowHyphen?: boolean }} opts */
function normalizeArabicLayoutToLatin(raw, { allowHyphen = false } = {}) {
  if (raw == null) return '';
  const s = String(raw);
  let out = '';
  const keepLatin = allowHyphen ? /[A-Za-z0-9-]/ : /[A-Za-z0-9]/;
  for (let i = 0; i < s.length; i += 1) {
    if (s.startsWith('لا', i)) {
      out += 'b';
      i += 1;
      continue;
    }
    const ch = s[i];
    if (keepLatin.test(ch)) {
      out += ch;
      continue;
    }
    const indicIdx = ARABIC_INDIC_TO_LATIN.indexOf(ch);
    if (indicIdx >= 0) {
      out += String(indicIdx);
      continue;
    }
    const mapped = ARABIC_KEYBOARD_CHAR_TO_LATIN[ch];
    if (mapped) {
      if (allowHyphen) {
        out += mapped;
      } else if (/[A-Za-z0-9]/.test(mapped)) {
        out += mapped;
      }
    }
  }
  return out;
}

/** Canonicalize R-{digits}-{suffix} from nextReceiptNum(). */
function applyReceiptFormatCanonicalization(out) {
  if (!out) return out;
  let s = out;
  if (/^[rR]/.test(s)) {
    s = `R${s.slice(1)}`;
  }
  const secondDash = s.indexOf('-', 2);
  if (secondDash === -1) return s;
  return `${s.slice(0, secondDash + 1)}${s.slice(secondDash + 1).toLowerCase()}`;
}

export function normalizeReceiptNumInput(raw) {
  return applyReceiptFormatCanonicalization(normalizeArabicLayoutToLatin(raw, { allowHyphen: true }));
}

/** Map Arabic keyboard / Indic digit output to a Latin alphanumeric refund code. */
export function normalizeRefundCodeInput(raw) {
  return normalizeArabicLayoutToLatin(raw, { allowHyphen: false });
}
